import { AxiosError, AxiosResponse } from "axios";
import { UserState } from "@/store/userStore";
import { useUserStore } from "@/store/userStore";
import DOMPurify from "dompurify";
import { useGlobalNaviStore } from "./store/grovalNaviStore";
import { useUserParmitionStore } from "./store/userParmitionStore";
import { useSiteSerchCategoryStore } from "./store/siteSerchCategoryStore";
import { ref } from "vue";
import { AppConstants } from "@/constants/AppConstants";
import apiAuthClient from "@/util/apiClientAuth";
import messages from "@/assets/messages.json";
import apiClientAuth from "@/util/apiClientAuth";
import { useIncentiveStore } from "./store/incentiveStore";
import logService from "@/services/logService";
import { parse, format } from "date-fns";
import Encoding from "encoding-japanese";
/**
 * テンプレート文字列内のプレースホルダーを指定された値で置換します。
 * プレースホルダーは {数字} の形式で表され、数字は `values` 配列のインデックスに対応します。
 * 指定されたインデックスに値が存在しない場合、プレースホルダーはそのままの文字列として残ります。
 *
 * @param {string} template - プレースホルダーを含む文字列テンプレート。
 * @param {...T[]} values - 置換する値の配列。各値はプレースホルダーに対応するインデックスで使用されます。
 * @returns {string} プレースホルダーが指定された値で置換された結果の文字列。
 *
 * @example
 * // returns "Hello, John! Are you 25 years old?"
 * console.log(formatMessage("Hello, {0}! Are you {1} years old?", "John", 25));
 */
export function formatMessage<T>(template: string, ...values: T[]) {
  return template.replace(/{(\d+)}/g, (match, index) => {
    const value = values[index];
    return value !== undefined && value !== null ? String(value) : match;
  });
}

export function outputDebugLogs(msg: string) {
  if (process.env.VUE_APP_DEBUG === "true") {
    console.log(msg);
  }
}

/**
 * 開発環境でのデバッグ用途に、ログインしたユーザーの詳細情報をコンソールに出力します。
 * この関数は環境変数 `VUE_APP_DEBUG` が "true" に設定されている場合のみ、
 * ユーザーの情報を出力します。それ以外の場合は何も出力しません。
 * ユーザー情報には、ユーザーID、AGコード、AG名、管理者権限の有無、インボイス番号などが含まれます。
 *
 * @param {UserState} userData - ログインしているユーザーの状態を保持するオブジェクト。
 *        このオブジェクトには、ユーザーに関連するさまざまな属性が含まれます。
 *
 * @example
 * // VUE_APP_DEBUG='true' で環境変数が設定されている場合にログイン時の情報がコンソールログが出力されます。
 */
export function outputLoginDebugLogs(userData: UserState) {
  if (process.env.VUE_APP_DEBUG === "true") {
    console.log("ログイン成功");
    console.log("ユーザーID：", userData.userId);
    console.log("AGコード", userData.agCode);
    console.log("AG名：", userData.agName);
    console.log("管理者権限有無：", userData.isAdmin);
    console.log("インボイス番号", userData.invoiceNo);
    console.log("Emailアドレス", userData.emailAddress);
    console.log("ロールID", userData.roleId);
    console.log("委託元AGコード", userData.itakumotoAgCode);
    console.log("代表コード", userData.daihyoCode);
    console.log("再委託先フラグ", userData.saiitakuFlg);
    console.log("担当者名", userData.userName);
    console.log("会社名", userData.companyName);
    console.log("部署名", userData.sectionName);
    console.log("備考１", userData.bikou1);
    console.log("備考２", userData.bikou2);
    console.log("ベースログインフラグ", userData.isBaseLogin);
  }
}

/**
 * Axiosのレスポンスから取得したユーザー情報を使用して、ローカルストレージにユーザー情報を登録します。
 * `useUserStore` フックから取得した `setUser` 関数を使用して、
 * ユーザー情報をローカルストレージに保存します。
 * この関数は、APIからユーザー情報を取得した後の処理に使用されます。
 *
 * @param {AxiosResponse} response - Axiosからのレスポンスオブジェクト。このオブジェクトの `data` プロパティには、
 *        ユーザー情報が含まれる `user` オブジェクトが格納されています。
 *
 * @example
 * // APIレスポンスからユーザー情報を設定
 * axios.get('/api/user/info').then(response => {
 *   setUserInfo(response);
 * });
 */
export function setUserInfo(response: AxiosResponse) {
  const { setUser } = useUserStore();
  setUser(
    response.data.user.userId,
    response.data.user.agCode,
    response.data.user.agName,
    response.data.user.isAdmin,
    response.data.user.invoiceNo,
    response.data.user.emailAddress,
    response.data.user.roleId,
    response.data.user.itakumotoAgCode,
    response.data.user.daihyoCode,
    response.data.user.saiitakuFlg,
    response.data.user.userName,
    response.data.user.companyName,
    response.data.user.sectionName,
    response.data.user.bikou1,
    response.data.user.bikou2,
    response.data.user.isBaseLogin,
    response.data.user.isKaunet
  );
}

/**
 * サニタイジング関数
 *
 * 入力文字列からHTML特殊文字をエスケープし、
 * XSS（クロスサイトスクリプティング）攻撃を防ぐためのサニタイジングを行います。
 *
 * @param input - サニタイジング対象の文字列
 * @returns サニタイジングされた文字列
 */
export const sanitizeInput = (
  input: string,
  replaceSpecialChars = false
): string => {
  let sanitizedInput = "";

  if (replaceSpecialChars) {
    // 特殊文字を全角文字に置換するマッピング
    const fullWidthMap: { [key: string]: string } = {
      "!": "！",
      "=": "＝",
      "&": "＆",
      "|": "｜",
      "<": "＜",
      ">": "＞",
      "(": "（",
      ")": "）",
      "[": "［",
      "]": "］",
      "{": "｛",
      "}": "｝",
      "~": "～",
      "*": "＊",
      '"': "”",
    };

    // 特殊文字を全角文字に置換
    sanitizedInput = input.replace(
      /[!=&|<>()[\]{}~*"]/g,
      (match) => fullWidthMap[match] || match
    );

    // DOMPurify で基本的なサニタイズを実行
    sanitizedInput = DOMPurify.sanitize(sanitizedInput);
  } else {
    // DOMPurify で基本的なサニタイズを実行
    sanitizedInput = DOMPurify.sanitize(input);
  }

  return sanitizedInput;
};

function getCurrentDate(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}/${mm}/${dd}`;
}

/**
 * バナーなどの取得対象日付を取得する関数
 *
 * 引数が空の場合は現在日付を返します
 *
 * @param input - 対象の日付
 * @returns 引数が空の場合、現在日付または営業日切替で設定されている日付 引数が設定されている場合は引数の日付を返却
 */
export const getTargetDate = (input: string): string => {
  if (input) {
    return input;
  } else {
    // ローカルストレージから営業日を取得
    const businessDate = localStorage.getItem(
      AppConstants.BUSINESS_DAYS_SESSION_NAME
    );

    const systemDate = localStorage.getItem(
      AppConstants.SYSTEMDATE_SESSION_NAME
    );

    if (businessDate) {
      return businessDate;
    } else {
      if (systemDate) {
        return localStorage.getItem(AppConstants.SYSTEMDATE_SESSION_NAME) || "";
      } else {
        return getCurrentDate();
      }
    }
  }
};

/**
 * ログイン時に各種情報を取得します
 *
 *
 * @param agCode - AGコード
 * @param userId - ユーザーID
 */
export const getLoginInfomation = async (
  agCode: string,
  userId: string,
  pseudoLoginFlg: boolean
) => {
  // エラーメッセージ用のリアクティブ変数
  const errorMessage = ref<string | null>(null);

  // グローバルナビデータを取得
  const { fetchItemsFromAPI } = useGlobalNaviStore();

  // デバックログ出力
  outputDebugLogs("グローバルナビデータ取得");

  const error = await fetchItemsFromAPI(agCode, userId, pseudoLoginFlg);

  if (error) {
    // デバックログ出力
    outputDebugLogs("グローバルナビデータ取得失敗：" + error);
    errorMessage.value = error;
    return errorMessage.value;
  }

  // デバックログ出力
  outputDebugLogs("グローバルナビデータ取得成功");

  // 権限情報を取得
  const { fetchParmitionItemsFromAPI } = useUserParmitionStore();

  // デバックログ出力
  outputDebugLogs("権限情報取得");

  const parmError = await fetchParmitionItemsFromAPI(agCode, userId);

  if (parmError) {
    // デバックログ出力
    outputDebugLogs("権限情報取得失敗：" + parmError);
    errorMessage.value = parmError;
    return errorMessage.value;
  }

  // デバックログ出力
  outputDebugLogs("権限情報取得成功");

  // デバックログ出力
  outputDebugLogs("サイト内検索のカテゴリ情報取得");

  // グローバルナビデータを取得
  const { fetchSiteSerchCagetoryFromAPI } = useSiteSerchCategoryStore();

  // デバックログ出力
  outputDebugLogs("サイト内検索のカテゴリ情報取得");

  const siteSerchError = await fetchSiteSerchCagetoryFromAPI(agCode, userId);

  if (siteSerchError) {
    // デバックログ出力
    outputDebugLogs("サイト内検索のカテゴリ情報取得失敗：" + siteSerchError);
    errorMessage.value = siteSerchError;
    return errorMessage.value;
  }

  // デバックログ出力
  outputDebugLogs("サイト内検索のカテゴリ情報取得成功");

  // インセンティブ情報取得
  const { fetchIncentiveFromAPI } = useIncentiveStore();

  // デバックログ出力
  outputDebugLogs("インセンティブ情報取得");

  const incentiveError = await fetchIncentiveFromAPI(agCode, userId);

  if (incentiveError) {
    // デバックログ出力
    outputDebugLogs("インセンティブ情報取得失敗：" + incentiveError);
    errorMessage.value = incentiveError;
    return errorMessage.value;
  }

  // デバックログ出力
  outputDebugLogs("インセンティブ情報取得成功");

  // 成功の場合はnullを返す
  return null;
};

/**
 * ローカルストレージからAGコードとユーザーIDを取得します。
 * 疑似ログイン情報が設定されている場合は、
 * 疑似ログインAGコードと疑似ログインユーザーIDを返却します。
 *
 * @returns agCode - AGコード、ユーザーID
 */
export function getUserInfo() {
  const agCode = ref<string | null>(null);
  const userId = ref<string | null>(null);

  const userInfo = localStorage.getItem(AppConstants.USER_SESSION_NAME);
  if (userInfo) {
    const parsedUserInfo = JSON.parse(userInfo);
    // 最初に疑似ログインAGコードとユーザーIDを検索用の変数にセット
    agCode.value = parsedUserInfo.pseudoAgCode;
    userId.value = parsedUserInfo.pseudoUserId;

    // 疑似ログインAGコードが入っていなかった場合は、ログインしているAGコードとユーザーID
    if (agCode.value === null || agCode.value === "") {
      agCode.value = parsedUserInfo.agCode;
      userId.value = parsedUserInfo.userId;
    }
  }

  return { agCode, userId };
}

/**
 * お気に入り登録・解除を行います
 *
 * @param categoryId - カテゴリID
 * @param contentsId - コンテンツID
 * @param isFavorite - True：お気に入り登録 False:お気に入り解除
 * @param screenName - 画面名
 */
export async function toggleFavoritePage(
  categoryId: string,
  contentsId: string,
  isFavorite: boolean,
  screenName: string
) {
  const userInfo = localStorage.getItem(AppConstants.USER_SESSION_NAME);
  const agCode = ref<string | null>(null);
  const userId = ref<string | null>(null);
  const parsedAgCode = ref<string | null>(null);
  const parsedUserId = ref<string | null>(null);

  if (!userInfo) {
    outputDebugLogs("ユーザーデータなし");
    return false;
  }

  const parsedUserInfo = JSON.parse(userInfo);
  agCode.value = parsedUserInfo.agCode;
  userId.value = parsedUserInfo.userId;
  parsedAgCode.value = parsedUserInfo.pseudoAgCode;
  parsedUserId.value = parsedUserInfo.pseudoUserId;

  try {
    outputDebugLogs("お気に入り登録解除");

    // お気に入りページ一覧取得API
    await apiAuthClient.post("/api/FavoritPage/toggleFavoritePage", {
      systemKbn: process.env.VUE_APP_SYSTEM_KBN,
      userId: userId.value,
      agCode: agCode.value,
      parsedUserId: parsedUserId.value,
      parsedAgCode: parsedAgCode.value,
      categoryId: categoryId,
      contentsId: contentsId,
      processType: isFavorite ? "2" : "1",
      screenName: screenName,
    });

    outputDebugLogs(isFavorite ? "お気に入り解除成功" : "お気に入り登録成功");
  } catch (error: unknown) {
    const axiosError = error as AxiosError;
    outputDebugLogs("Error: " + axiosError.message);
    return false;
  } finally {
    //何か入れるかも
  }

  return true;
}

/**
 * エラーメッセージを取得し、プレースホルダーを置き換えます。
 * @param errorCode - エラーコード
 * @param placeholders - プレースホルダーに置き換える値
 * @returns エラーメッセージ
 */
export function getErrorMessage(
  errorCode: string,
  ...placeholders: string[]
): string {
  const messageTemplate = messages[errorCode as keyof typeof messages];
  if (!messageTemplate) {
    return "Unknown error";
  }

  return messageTemplate.replace(/{(\d+)}/g, (match, index) => {
    return typeof placeholders[index] !== "undefined"
      ? placeholders[index]
      : match;
  });
}

/**
 * 画面のスクロールを最上部に戻します
 */
export function scrollToTop() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

/**
 * お気に入り登録・解除を行います
 *
 * @param agCode - AGコード
 * @param userId - ユーザーID
 * @param categoryId - カテゴリID
 * @param contentsId - コンテンツID
 */
export async function regRecentlyViewedHistory(
  agCode: string,
  userId: string,
  categoryId: string,
  contentsId: string
) {
  try {
    outputDebugLogs("閲覧履歴登録");

    // 閲覧履歴登録API
    await apiAuthClient.post("/api/RecentlyViewed/regRecentlyViewedHistory", {
      systemKbn: process.env.VUE_APP_SYSTEM_KBN,
      agCode: agCode,
      userId: userId,
      categoryId: categoryId,
      contentsId: contentsId,
    });

    outputDebugLogs("閲覧履歴登録成功");
  } catch (error: unknown) {
    const axiosError = error as AxiosError;
    outputDebugLogs("Error: " + axiosError.message);
    return false;
  } finally {
    //何か入れるかも
  }

  return true;
}

/**
 * テキスト内に基本多言語面外の文字が含まれているかをチェックする関数
 * @param text - チェックするテキスト
 * @returns 基本多言語面外の文字が含まれていなければ true、それ以外は false
 */
export function hasNoGaiji(text: string): boolean {
  for (const char of text) {
    const codePoint = char.codePointAt(0);
    if (codePoint !== undefined && codePoint > 0xffff) {
      return false;
    }
  }
  return true;
}

const forbiddenChars = ["'", "<", ">", "&", "(", ")", ",", "{", "}"]; // 禁則文字

/**
 * ファイル名チェック
 * @param input ファイル名
 */
export function checkFileName(input: string): boolean {
  if (input.length > 50) {
    return false;
  }

  for (const char of forbiddenChars) {
    if (input.includes(char)) {
      return false;
    }
  }

  /* SJIS 未対応文字判定 */
  if (!isEncodableToSJIS(input)) {
    return false;
  }

  return true;
}

/**
 * トークン有効性チェック
 * @returns true:トークン有効 false:トークン無効
 */
export async function isTokenAlive(): Promise<boolean> {
  const userInfo = localStorage.getItem(AppConstants.USER_SESSION_NAME);

  try {
    if (userInfo) {
      const userId = JSON.parse(userInfo).userId;

      const response = await apiClientAuth.post("/api/auth/validate", userId, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (response.data.isValid) {
        outputDebugLogs("トークン有効");

        // ローカルストレージのトークンを書き換え
        localStorage.setItem(
          AppConstants.TOKEN_SESSION_NAME,
          response.data.token
        );

        return true;
      } else {
        outputDebugLogs("トークン無効");
        return false;
      }
    } else {
      return false;
    }
  } catch (error) {
    outputDebugLogs("エラー内容:" + error);
    outputDebugLogs("トークン無効");
    return false;
  }
}

/**
 * キーワードから空白を削除します
 * @param keyword 対象の文字列
 * @returns 空白を削除した文字列
 */
export function trimKeyword(keyword: string): string {
  const trimKeyword = keyword.replace(/[\s\u3000]/g, "");
  return trimKeyword;
}

/**
 * 指定されたキーワードから半角および全角スペースを削除してから
 * キーワードが入力されているかチェックします
 * @param keyword チェック対象文字列
 * @returns true:入力されている false:未入力
 */
export function isInputKeyword(keyword: string): boolean {
  const trimTarget = trimKeyword(keyword);
  // 入力チェック
  if (trimTarget) {
    return true;
  } else {
    return false;
  }
}

/**
 * ログインのURLを取得します
 * @returns ログインURL
 */
export function getLoginPath(): string {
  return process.env.VUE_APP_ADDROOT_PATH + "/login";
}

/**
 * ポータルトップのURLを取得します
 * @returns ポータルトップURL
 */
export function getPortalTopPath(): string {
  return process.env.VUE_APP_ADDROOT_PATH + "/portal-top";
}

export function replaceSpanWithColorCode(htmlContent: string): string {
  return htmlContent.replace(
    /<span style="color:\s*rgb\((\d+),\s*(\d+),\s*(\d+)\);?">(.*?)<\/span>/g,
    (match, r, g, b, text) => `*${r}, ${g}, ${b}*${text}`
  );
}

export function replaceAsteriskColorWithSpan(htmlContent: string): string {
  return htmlContent.replace(
    /\*(\d+),\s*(\d+),\s*(\d+)\*(.*?)($|\s|<\/p>|<br>)/g,
    (match, r, g, b, text) =>
      `<span class="color-ql-${r}_${g}_${b}">${text}</span>`
  );
}

export function replaceInlineStyleWithClass(htmlContent: string): string {
  // 既存の class="color-ql-..." を削除
  const cleanedContent = htmlContent.replace(
    /class="color-ql-\d+_\d+_\d+"/g,
    ""
  );

  return cleanedContent.replace(
    /style="color:\s*rgb\((\d+),\s*(\d+),\s*(\d+)\);?"/g,
    (match, r, g, b) => `class="color-ql-${r}_${g}_${b}"`
  );
}

export function replaceClassWithInlineStyle(htmlContent: string): string {
  return htmlContent.replace(
    /class="color-ql-(\d+)_(\d+)_(\d+)"/g,
    (match, r, g, b) => `style="color: rgb(${r}, ${g}, ${b});"`
  );
}

export function addInlineStyleToClass(htmlContent: string): string {
  return htmlContent.replace(
    /class="color-ql-(\d+)_(\d+)_(\d+)"/g,
    (match, r, g, b) =>
      `style="color: rgb(${r}, ${g}, ${b});" class="color-ql-${r}_${g}_${b}"`
  );
}

export function convertStyleToClass(htmlString: string): string {
  // 正規表現で color: rgb(...) スタイルを探す
  return htmlString.replace(
    /style="color:\s*rgb\((\d+),\s*(\d+),\s*(\d+)\);?"/g,
    function (match, r, g, b) {
      // 新しいクラス名を作成
      const className = `color-ql-${r}_${g}_${b}`;
      // styleをclassに変換
      return `class="${className}"`;
    }
  );
}

export function convertClassToStyle(htmlString: string): string {
  // 正規表現で class="color-ql-..." を探す
  return htmlString.replace(
    /class="color-ql-(\d+)_(\d+)_(\d+)"/g,
    function (match, r, g, b) {
      // RGB値を使ってstyle属性を生成
      const styleAttribute = `style="color: rgb(${r}, ${g}, ${b});"`;
      return styleAttribute;
    }
  );
}

/**
 * リンククリックのログを出力します
 * @param linkUrl リンクURL
 */
export function regLinkClickLog(
  linkUrl: string,
  userId: string,
  agCode: string,
  pseudoAgCode: string,
  pseudoUserId: string,
  contentsId: string,
  screenName: string
) {
  const genericField2 = "リンクURL：" + linkUrl;
  regLogData(
    AppConstants.OP_TYPE_LINK_CLICK,
    genericField2,
    "リンククリック",
    userId,
    agCode,
    pseudoAgCode,
    pseudoUserId,
    contentsId,
    screenName
  );
}

/**
 * ファイルリンククリックのログを出力します
 * @param fileName ファイル名
 */
export function regFileLinkClickLog(
  fileName: string,
  userId: string,
  agCode: string,
  pseudoAgCode: string,
  pseudoUserId: string,
  contentsId: string,
  screenName: string
) {
  const genericField2 = "ファイル名：" + fileName;
  regLogData(
    AppConstants.OP_TYPE_VIEW_FILE,
    genericField2,
    "ファイルリンククリック",
    userId,
    agCode,
    pseudoAgCode,
    pseudoUserId,
    contentsId,
    screenName
  );
}

/**
 * ログデータ出力処理
 * @param operationType 操作内容
 * @param genericField2 汎用項目２
 * @param actionDetail  操作詳細
 */
export function regLogData(
  operationType: string,
  genericField2: string,
  actionDetail: string,
  userId: string,
  agCode: string,
  pseudoAgCode: string,
  pseudoUserId: string,
  contentsId: string,
  screenName: string
) {
  // ログを出力
  const logData = {
    systemkbn: process.env.VUE_APP_SYSTEM_KBN,
    userId: userId,
    agCode: agCode,
    actionTime: new Date().toISOString(),
    operationType: operationType,
    pseudoAgCode: pseudoAgCode,
    pseudoUserId: pseudoUserId,
    fromScreenName: screenName,
    toScreenName: "",
    controlName: "",
    actionDetail: actionDetail,
    genericField1: "コンテンツID：" + contentsId,
    genericField2: genericField2,
    genericField3: "",
    currentUrl: window.location.href,
  };

  logService.sendLog(logData);
}

interface ErrorResponse {
  messages: ErrorMessage[];
}

interface ErrorMessage {
  messageId: string;
  args: string[];
}

/**
 * AxiosErrorを処理します。
 * @param error エラー情報
 */
export function commonHandleAxiosError(
  error: unknown,
  isAdmin: boolean
): string {
  const axiosError = error as AxiosError;
  let errorMessage = "";
  if (axiosError.response) {
    outputDebugLogs("axiosError.response.status:" + axiosError.response.status);
    outputDebugLogs(
      "typeof axiosError.response.data:" + typeof axiosError.response.data
    );
    switch (axiosError.response.status) {
      case 400:
        if (typeof axiosError.response.data == "string") {
          // 文字列の場合
          if (!axiosError.response.data) {
            if (isAdmin) {
              errorMessage = messages.ERR_SYS998;
            } else {
              errorMessage = messages.ERR_SYS999;
            }
          } else {
            errorMessage = axiosError.response.data;
          }
        } else if (typeof axiosError.response.data == "object") {
          // オブジェクトの場合
          const errorResponse = axiosError.response.data as ErrorResponse;
          outputDebugLogs("errorResponse.messages:" + errorResponse.messages);
          if (!errorResponse.messages) {
            outputDebugLogs("!errorResponse.messages");
            if (isAdmin) {
              errorMessage = messages.ERR_SYS998;
            } else {
              errorMessage = messages.ERR_SYS999;
            }
          } else {
            errorResponse.messages.forEach((message, index) => {
              if (index > 0) {
                errorMessage += String.fromCharCode(10);
              }
              errorMessage += getErrorMessage(
                message.messageId,
                ...message.args
              );
              outputDebugLogs("errorMessage" + errorMessage);
            });
          }
        }
        break;
      case 403:
        // Forbiddenエラー
        errorMessage = messages.ERR_0022;
        break;
      default:
        // ほかのエラー
        if (isAdmin) {
          errorMessage = messages.ERR_SYS998;
        } else {
          errorMessage = messages.ERR_SYS999;
        }
    }
  } else if (axiosError.request) {
    errorMessage = messages.ERR_SYS001;
  } else {
    errorMessage = messages.ERR_SYS002;
    if (process.env.VUE_APP_DEBUG === "true") {
      console.log("Error: ", axiosError.message);
    }
  }

  return errorMessage;
}

/**
 * 指定した文字列の日付を ISO8601形式 (例: 2024-12-26T14:52:00) に変換する関数
 * @param strDate - 変換対象の日付文字列(例: '2024-12-26 14:52')
 * @param originalFormat - strDateの元のフォーマット (例: 'yyyy-MM-dd HH:mm')
 * @returns ISO8601形式の文字列 (例: '2024-12-26T14:52:00')
 */
export const formatISO8601DateTime = (
  strDate: string,
  originalFormat = "yyyy-MM-dd HH:mm"
): string => {
  if (!strDate) return "";

  strDate = formatDateTime(strDate);
  // 1) strDate を parse で Date 型に変換する
  const date = parse(strDate, originalFormat, new Date());

  // 2) 変換した Date を ISO8601 形式（秒まで）にフォーマットして返す
  //    例: '2024-12-26T14:52:00'
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
};

/**
 * 日付をyyyy-MM-dd HH:mm形式にフォーマットします。
 */
export const formatDateTime = (strDate: string): string => {
  if (strDate) {
    return format(strDate, "yyyy-MM-dd HH:mm");
  }
  return "";
};

export const formatDate = (strDate: string): string => {
  if (strDate) {
    return format(strDate, "yyyy-MM-dd");
  }
  return "";
};

/**
 * 表示用の形式に日付型を変換します
 */
export const formatDispDate = (strDate: string): string => {
  if (strDate) {
    return format(strDate, "yyyy/MM/dd");
  }
  return "";
};

/**
 * 日付が有効な場合は日付、無効な場合はnullを返却します。
 */
export const getValidStrDate = (strDate: string): string | null => {
  if (strDate) {
    return strDate;
  }
  return null;
};

/**
 * バイト数を計算する関数
 * @param input - チェックしたい文字列
 * @returns 文字列のバイト数
 */
export const getByteLength = (input: string): number => {
  // UTF-8エンコーディングを考慮したバイト数計算
  return new TextEncoder().encode(input).length;
};

/**
 * バイト数が指定範囲内かを検証する関数
 * @param input - チェックしたい文字列
 * @param minBytes - バイト数の最小値
 * @param maxBytes - バイト数の最大値
 * @returns 範囲内かどうかの真偽値
 */
export const isByteLengthWithinRange = (
  input: string,
  minBytes: number,
  maxBytes: number
): boolean => {
  const byteLength = getByteLength(input);
  return byteLength >= minBytes && byteLength <= maxBytes;
};

// U+0300-036F 結合記号 + U+3099, U+309A (濁点/半濁点)
const COMBINING_MARK = /[\u0300-\u036F\u3099\u309A]/;

export function containsCombiningMark(str: string): boolean {
  return COMBINING_MARK.test(str);
}

/**
 * 指定した文字列が Shift_JIS（JA16SJISTILDE）に
 * 完全にマッピング可能かどうかを判定する。
 *
 * 判定ロジック
 *  1. 入力を NFC 正規化（結合文字 → 単一コード化）
 *  2. UTF-16 コードポイント配列へ変換
 *  3. Shift_JIS バイト列へ変換
 *  4. そのバイト列を再び UTF-16 へ戻す
 *  5. 1. の正規化済み文字列と一致すれば「Lossless」＝ encodable
 *
 * @param name ファイル名など判定対象の文字列
 * @returns true  : SJIS に完全変換可能
 *          false : SJIS で欠落 or 変形が発生（→文字化けの恐れ）
 */
export function isEncodableToSJIS(name: string): boolean {
  // 先制ブロック
  if (containsCombiningMark(name)) {
    return false;
  }

  const nfc = name.normalize("NFC");

  const utf16 = Encoding.stringToCode(nfc);
  const sjis = Encoding.convert(utf16, "SJIS", "UNICODE");

  // Win-31J → Oracle 方言差を考慮して、
  //    変換後に "?"(0x3F) や U+FFFD が含まれたら NG
  const hasFallback = sjis.some((b) => b === 0x3f); // "?"
  if (hasFallback) return false;

  const roundTrip = Encoding.convert(sjis, "UNICODE", "SJIS");
  const roundStr = Encoding.codeToString(roundTrip);

  // 完全一致で OK
  return roundStr === nfc;
}
